import moment from "moment";
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { DiaryGridModel } from "../../../../app/models/DiaryGridModel";
import { TagSettingsSS } from "../../../../app/models/ContactModel";
import {
  ContactContactS,
  PinnedNoteModel,
  OfficeDefaultTagModel,
  ExtendedContactModel,
} from "../../../../app/models/ContactOverviewModel";
import {
  CalendarClock,
  CheckSquare,
  Pin,
  Square,
  SquareKanban,
} from "lucide-react";
import { Badge } from "@/app/common/common-components/ui/Badge";
import { cn } from "@/app/common/common-functions/functions";
import RequirementsList from "./RequirementsList";
import apiAgent from "../../../../app/api/apiAgent";
import NewLoader from "@/app/layout/NewLoader";
import PipelineStageDropdownMenu from "@/features/deal/dealComponents/shared/PipelineStageDropdownMenu";
import { useContacts } from "../../../../app/hooks/useContacts";
import { toast } from "react-toastify";
import NewMessageButton from "@/features/contact/SaveContactTabs/MessageTab/NewMessageButton";
import NewMessageDialog from "@/features/contact/SaveContactTabs/MessageTab/NewMessageDialog";
import { useStore } from "@/app/stores/store";
import { Separator } from "@/app/common/common-components/ui/Separator";

const recurringEventTypes = {
  "0": "Does not repeat",
  "1": "Daily",
  "2": "Weekly",
  "3": "Monthly",
  "4": "Yearly",
};

export interface ContactOverviewProps {
  diary: DiaryGridModel;
  pinnedNotes?: PinnedNoteModel[];
  unpinNote?: (note: PinnedNoteModel) => void;
  pinDisabled?: string;
  leadSource: string;
  contact: ExtendedContactModel;
  userDefinedTags: TagSettingsSS[];
  officeDefaultTags: OfficeDefaultTagModel[];
  status: number;
  mContact: ContactContactS;
  renderDiaryItems?: () => React.ReactNode;
}

function ContactOverview({
  diary,
  pinnedNotes,
  unpinNote,
  pinDisabled,
  leadSource,
  contact,
  userDefinedTags,
  officeDefaultTags,
  status,
  mContact,
}: ContactOverviewProps) {
  const { updateMilestone } = useContacts();
  const queryClient = useQueryClient();
  const [newMessageDialogOpen, setNewMessageDialogOpen] = useState(false);
  const [initialMessage, setInitialMessage] = useState<{
    type: "Email" | "SMS" | "Whatsapp";
    date: string;
    message: string;
    subject?: string;
  } | null>(null);
  const {
    userStore: { userSession },
  } = useStore();
  const userId = userSession?.userId;
  const { data: diaryItems, status: diaryStatus } = useQuery({
    queryKey: ["diaryItems", contact.contactID, 1],
    queryFn: () => apiAgent.Diary.timelineDiariesAjax(contact.contactID, 1),
    enabled: !!contact.contactID,
  });

  const diaryItemsToDisplay =
    diaryItems
      ?.filter(
        (item: DiaryGridModel) => item.typeID !== "Note" && item.isUpcoming
      )
      .sort((a: DiaryGridModel, b: DiaryGridModel) => {
        return moment(a.startDate).diff(moment(b.startDate));
      })
      .reduce(
        (
          acc: {
            recurringEventID?: string;
            frequencyID?: string;
            diaryItems: DiaryGridModel[];
            firstItem: DiaryGridModel;
          }[],
          item: DiaryGridModel
        ) => {
          const existingEvent = item.recurringEventID
            ? acc.find(
                (event) => event.recurringEventID === item.recurringEventID
              )
            : null;
          if (existingEvent) {
            existingEvent.diaryItems.push(item);
            if (existingEvent.firstItem.startDate > item.startDate) {
              existingEvent.firstItem = item;
            }
          } else {
            acc.push({
              recurringEventID: item.recurringEventID,
              diaryItems: [item],
              frequencyID: item.recurringFrequencyID,
              firstItem: item,
            });
          }
          return acc;
        },
        []
      )
      .slice(0, 5) || [];

  console.log(diaryItemsToDisplay);

  const updateContactStageMutation = useMutation({
    mutationFn: async ({
      contactId,
      pipelineId,
      stageId,
    }: {
      contactId: string;
      pipelineId: string;
      stageId: string;
    }) => {
      return await updateMilestone(contactId, pipelineId, stageId);
    },
    onSuccess: () => {
      toast.success("Contact stage updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["contact", contact.contactID],
      });
    },
    onError: (error) => {
      toast.error("Failed to update contact stage");
    },
  });

  const handleStageClick = (pipelineId: string, stageId: string) => {
    if (contact.contactID) {
      updateContactStageMutation.mutate({
        contactId: contact.contactID,
        pipelineId,
        stageId,
      });
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {/* Pipeline & milestones */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <SquareKanban className="tw-size-6" />
        <PipelineStageDropdownMenu
          type="contact"
          officeId={contact.officeID}
          title="Move to pipeline"
          onStageClick={handleStageClick}
        >
          <div className="tw-cursor-pointer hover:tw-underline">
            {contact.pipelineName || "No pipeline"} |{" "}
            {contact.stageName || "No stage"}
          </div>
        </PipelineStageDropdownMenu>
      </div>
      {/* Icons to Phone, email, whatsapp */}
      <NewMessageButton
        handleNewMessageClick={(messageType) => {
          setInitialMessage({
            type: messageType,
            date: new Date().toISOString(),
            message: "",
            subject: "",
          });
          setNewMessageDialogOpen(true);
        }}
        variant="icons"
        contactPhone={
          contact.contact
            ? JSON.parse(contact.contact).phone_dialing_code +
              JSON.parse(contact.contact).phone
            : ""
        }
      />
      <NewMessageDialog
        open={newMessageDialogOpen}
        onOpenChange={setNewMessageDialogOpen}
        contact={contact}
        initialMessage={initialMessage}
        variables={[
          {
            label: "Contact First Name",
            value: contact.firstName || "",
            replace: "{{contact_first_name}}",
          },
          {
            label: "Contact Last Name",
            value: contact.lastName || "",
            replace: "{{contact_last_name}}",
          },
          {
            label: "Contact Email",
            value: contact.emailAddress || "",
            replace: "{{contact_email}}",
          },
          {
            label: "Contact Phone",
            value: contact.contact
              ? JSON.parse(contact.contact).phone_dialing_code +
                JSON.parse(contact.contact).phone
              : "",
            replace: "{{contact_phone}}",
          },
          {
            label: "Agent First Name",
            value: userSession?.firstName || "",
            replace: "{{agent_first_name}}",
          },
          {
            label: "Agent Last Name",
            value: userSession?.lastName || "",
            replace: "{{agent_last_name}}",
          },
          {
            label: "Agent Email",
            value: userSession?.emailAddress || "",
            replace: "{{agent_email}}",
          },
        ]}
        userId={userId!}
      />
      {/* Source, Status, Tags */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
        <div className="tw-flex tw-items-center tw-gap-1">
          <Badge variant="secondary" className="tw-text-xs">
            {leadSource}
          </Badge>

          {status === 1 ? (
            <CheckSquare className="tw-size-4" />
          ) : (
            <Square className="tw-size-4" />
          )}
          <span>{status === 1 ? "Active" : "Inactive"}</span>
        </div>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-overflow-hidden">
          <div className="tw-flex tw-flex-wrap tw-gap-1">
            {officeDefaultTags.map(function (item: any) {
              return item.linked ? (
                <span
                  key={item.name}
                  className="tw-rounded tw-px-2 tw-py-1 tw-text-xs tw-text-white"
                  style={{
                    backgroundColor: item.color,
                  }}
                >
                  {item.name} on{" "}
                  <Link
                    to={`/Property/SaveProperty?ID=${item.linkedobj.PropertyID}`}
                    target="_blank"
                    className="tw-text-white tw-underline"
                  >
                    {item.linkedobj.DisplayName}
                  </Link>
                </span>
              ) : item.selected ? (
                <span
                  key={item.name}
                  className="tw-rounded tw-px-2 tw-py-1 tw-text-xs tw-text-white"
                  style={{
                    backgroundColor: item.color,
                  }}
                >
                  {item.name}
                </span>
              ) : null;
            })}
            {userDefinedTags.map(function (item: any) {
              return item.selected ? (
                <span
                  key={item.name}
                  className="tw-rounded tw-px-2 tw-py-1 tw-text-xs tw-text-white"
                  style={{
                    backgroundColor: item.color,
                  }}
                >
                  {item.name}
                </span>
              ) : null;
            })}
          </div>
        </div>
      </div>
      {diaryStatus === "pending" && (
        <>
          <Separator />
          <NewLoader iconOnly />
        </>
      )}
      {pinnedNotes &&
        diaryStatus === "success" &&
        diaryItemsToDisplay.length > 0 && (
          <>
            <Separator />
            {/* Pinned Notes */}
            {pinnedNotes && (
              <div className="tw-flex tw-flex-wrap tw-gap-3">
                {pinnedNotes.map((note: any) => (
                  <div
                    key={note.diaryID}
                    className={cn(
                      "tw-flex tw-size-fit tw-justify-between tw-gap-2 tw-bg-yellow-100 tw-p-3",
                      pinDisabled === note.diaryID ? "tw-opacity-50" : ""
                    )}
                  >
                    <div
                      className="tw-size-fit tw-cursor-pointer tw-rounded tw-p-1 hover:tw-bg-gray-100"
                      onClick={() =>
                        pinDisabled !== note.diaryID && unpinNote
                          ? unpinNote(note)
                          : null
                      }
                      title="Unpin Note"
                    >
                      <Pin className="tw-size-4" />
                    </div>
                    <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
                      <div>
                        <div className="tw-font-semibold">
                          {note.diaryTitle}
                        </div>
                        <div className="tw-text-xs tw-text-gray-600">
                          {moment(note.startDate, "DD-MM-YYYY hh:mm A").format(
                            "DD MMM YYYY"
                          )}
                          {note.endDate
                            ? ` - ${moment(note.endDate, "DD-MM-YYYY hh:mm A").format("DD MMM YYYY")}`
                            : ""}
                        </div>
                      </div>
                      <p className="tw-text-sm">{note.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Diary Items */}
            <div className="tw-ml-2">
              {(!diaryItemsToDisplay || diaryItemsToDisplay.length === 0) && (
                <div>No diary events found</div>
              )}
              {diaryItemsToDisplay.length > 0 && (
                <div className="tw-space-y-2">
                  {diaryItemsToDisplay.map((item, index) => (
                    <div
                      key={index}
                      className="tw-flex tw-items-center tw-gap-1"
                    >
                      <CalendarClock className="tw-size-4" />
                      <span className="tw-font-semibold">
                        {item.firstItem.typeID}{" "}
                      </span>
                      {item.firstItem.startDate} {item.firstItem.contactName}
                      {item.frequencyID && item.frequencyID !== "0" && (
                        <Badge variant="outline" className="tw-text-xs">
                          {
                            recurringEventTypes[
                              item.frequencyID as keyof typeof recurringEventTypes
                            ]
                          }
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

      <Separator />
      <RequirementsList contactID={contact.contactID} />
    </div>
  );
}

export default ContactOverview;
